import React, {useState} from 'react'
import {Spinner, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {Flow, FlowStatus, IFlowStatisticsSummary} from '../WorkFlow/FlowTypes'
import {useGetInstanceLogsStatusQuery} from '../../../../../services/cellcast.v2'
import './FlowMobileCard.scss'

interface FlowMobileAction {
  key: string
  label: string
  icon?: string
  danger?: boolean
  onClick: () => void
}

interface FlowMobileCardProps {
  flow: Flow
  isAdmin: boolean
  navigate: (path: string, opts?: any) => void
  handleNavigateToInstanceLogs: (id: string) => void
  handleNavigateToReport: (id: string) => void
  handleToggleStatus: (id: string, status: FlowStatus) => void
  setSelectedId: (id: string) => void
  setOpen: (open: boolean) => void
  setResumeModalOpen: (open: boolean) => void
  setResumeFlowId: (id: string) => void
  setResumeFlowName: (name: string) => void
  isUpdating: boolean
  updatingId?: string | null
  formatDate: (date: string) => string
  FlowStatus: typeof FlowStatus
  t: (key: string) => string
  handleDuplicateFlow: (id: string) => Promise<void>
  handleOpenSettings: (flow: Flow) => void
}

export const FlowMobileCard: React.FC<FlowMobileCardProps> = ({
  flow,
  isAdmin,
  navigate,
  handleNavigateToInstanceLogs,
  handleNavigateToReport,
  handleToggleStatus,
  setSelectedId,
  setOpen,
  setResumeModalOpen,
  setResumeFlowId,
  setResumeFlowName,
  isUpdating,
  updatingId,
  formatDate,
  FlowStatus,
  t,
  handleDuplicateFlow,
  handleOpenSettings,
}) => {
  const [showActions, setShowActions] = useState(false)

  const {data: statusData, isLoading: isStatusLoading} = useGetInstanceLogsStatusQuery({
    id: flow._id,
  })

  const statusBreakdown = (statusData?.data?.statistics as IFlowStatisticsSummary)?.statusBreakdown

  const handleActionClick = () => {
    setShowActions(!showActions)
  }

  const actions: FlowMobileAction[] = [
    {
      key: 'view-report',
      label: 'View Report',
      icon: 'bi bi-bar-chart',
      onClick: () => {
        if (!isAdmin) {
          handleNavigateToReport(flow._id)
        }
      },
    },
    {
      key: 'view-activity',
      label: 'View Activity',
      icon: 'bi bi-eye',
      onClick: () => {
        if (!isAdmin) {
          handleNavigateToInstanceLogs(flow._id)
        }
      },
    },
    {
      key: 'edit',
      label: 'Edit',
      icon: 'bi bi-pencil-square',
      onClick: () => {
        isAdmin
          ? navigate(`/templates/automation/flow/${flow._id}`)
          : navigate(`/automation/flow/${flow._id}`)
      },
    },
    {
      key: 'duplicate',
      label: 'Duplicate',
      icon: 'bi bi-files',
      onClick: () => {
        handleDuplicateFlow(flow._id)
        setShowActions(false)
      },
    },
    {
      key: 'settings',
      label: 'Settings',
      icon: 'bi bi-gear',
      onClick: () => {
        handleOpenSettings(flow)
        setShowActions(false)
      },
    },
    {
      key: 'toggle-status',
      label: flow.status === FlowStatus.ACTIVE ? 'Pause' : 'Play',
      icon: flow.status === FlowStatus.ACTIVE ? 'bi bi-pause-circle' : 'bi bi-play-circle',
      onClick: () => {
        const newStatus =
          flow.status === FlowStatus.ACTIVE ? FlowStatus.INACTIVE : FlowStatus.ACTIVE
        handleToggleStatus(flow._id, newStatus)
        setShowActions(false)
      },
    },
  ]

  if (flow.haveJobPaused) {
    actions.push({
      key: 'resume',
      label: 'Resume Flow',
      icon: 'bi bi-arrow-clockwise',
      onClick: () => {
        setResumeFlowId(flow._id)
        setResumeFlowName(flow.name)
        setResumeModalOpen(true)
        setShowActions(false)
      },
    })
  }

  actions.push({
    key: 'delete',
    label: t('DELETE'),
    icon: 'bi bi-trash',
    danger: true,
    onClick: () => {
      setSelectedId(flow._id)
      setOpen(true)
      setShowActions(false)
    },
  })

  return (
    <div className='flow-mobile-card'>
      {/* Header with title and actions */}
      <div className='card-header-section'>
        <div className='flow-info-section'>
          <h5 className='flow-name'>{flow.name}</h5>
        </div>
        <button
          className='btn btn-action-mobile'
          onClick={handleActionClick}
          aria-label='Actions menu'
          aria-expanded={showActions}
        >
          <i className='bi bi-three-dots-vertical'></i>
        </button>
      </div>

      {/* Actions dropdown */}
      {showActions && (
        <div className='actions-dropdown'>
          {actions.map((action) => (
            <button
              key={action.key}
              className={`action-item ${action.danger ? 'text-danger' : ''}`}
              onClick={() => {
                action.onClick()
              }}
            >
              {action.icon && <i className={`${action.icon} me-2`}></i>}
              {action.label}
            </button>
          ))}
        </div>
      )}

      {/* Flow details grid */}
      <div className='card-details-grid'>
        {/* Status */}
        <div className='detail-item'>
          <span className='detail-label'>
            <i className='bi bi-info-circle me-1'></i>
            Status
          </span>
          <span className='detail-value'>
            <div className='d-flex align-items-center gap-2'>
              {flow.status === FlowStatus.ACTIVE ? (
                <i className='bi bi-play-circle text-success'></i>
              ) : (
                <i className='bi bi-stop-circle text-muted'></i>
              )}
              <span>{flow.status === FlowStatus.ACTIVE ? 'Active' : 'Inactive'}</span>
            </div>
          </span>
        </div>

        {/* Bites */}
        <div className='detail-item'>
          <span className='detail-label'>
            <i className='bi bi-lightning-charge me-1'></i>
            Bites
          </span>
          <span className='detail-value'>{statusBreakdown?.bites || '-'}</span>
        </div>

        {/* Contacts */}
        <div className='detail-item full-width'>
          <span className='detail-label'>
            <i className='bi bi-people me-1'></i>
            Contacts
          </span>
          {isStatusLoading ? (
            <Spinner size='sm' />
          ) : (
            <div className='detail-value d-flex flex-column gap-1 align-items-start text-start'>
              <span className='text-muted fs-8'>Started: {statusBreakdown?.started ?? '-'}</span>
              <span className='text-muted fs-8'>In Progress: {statusBreakdown?.running ?? '-'}</span>
              <span className='text-muted fs-8'>Completed: {statusBreakdown?.completed ?? '-'}</span>
              <span className='text-muted fs-8'>Paused: {statusBreakdown?.paused ?? '-'}</span>
            </div>
          )}
        </div>

        {/* Errors */}
        <div className='detail-item full-width'>
          <span className='detail-label'>
            <i className='bi bi-exclamation-triangle me-1'></i>
            Status
          </span>
          <span className='detail-value'>
            {flow.haveJobPaused ? (
              <div className='d-flex align-items-center'>
                <OverlayTrigger
                  placement='top'
                  overlay={<Tooltip>{'Flow paused due to insufficient credits.'}</Tooltip>}
                >
                  <i className='bi bi-info-circle me-2 text-danger'></i>
                </OverlayTrigger>
                <span className='text-danger'>Flow paused, requires attention</span>
              </div>
            ) : (
              <OverlayTrigger
                placement='top'
                overlay={<Tooltip>{'Working fine. No more actions needed.'}</Tooltip>}
              >
                <div className='d-flex align-items-center'>
                  <i className='bi bi-check-circle me-2 text-success'></i>
                  <span>Working fine</span>
                </div>
              </OverlayTrigger>
            )}
          </span>
        </div>

        {/* Time Created */}
        <div className='detail-item'>
          <span className='detail-label'>
            <i className='bi bi-calendar3 me-1'></i>
            Created
          </span>
          <span className='detail-value'>{formatDate(flow.createdAt)}</span>
        </div>

        {/* Last Edited */}
        <div className='detail-item'>
          <span className='detail-label'>
            <i className='bi bi-pencil me-1'></i>
            Last Edited
          </span>
          <span className='detail-value'>
            {flow.updatedAt ? formatDate(flow.updatedAt) : '-'}
          </span>
        </div>
      </div>

      {/* Action buttons */}
      <div className='card-actions'>
        {!isAdmin && (
          <button
            className='btn btn-light-primary btn-sm'
            onClick={() => handleNavigateToReport(flow._id)}
          >
            <i className='bi bi-bar-chart me-2'></i>
            View Report
          </button>
        )}
        <button
          className='btn btn-light btn-sm'
          onClick={() => {
            if (!isAdmin) {
              navigate(`/automation/contacts-in-flows/${flow._id}`, {state: {flow}})
            }
          }}
        >
          <i className='bi bi-eye me-2'></i>
          View Details
        </button>
      </div>
    </div>
  )
}

